import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { API } from "../../config";
import Spin from "../Spinner/Spinner";
import styles from "./Edit.module.css";
import { useDropzone } from "react-dropzone";
import { MdCloudUpload } from "react-icons/md";
import { TextEditor } from "./TextEditor";
import { DeleteConfirmation } from "./DeleteConfirmation";
import Toggle from "../../utils/Toggle/Toggle";

const FormFields = [
  {
    title: "Heading",
    placeholder: "Enter Blog Heading",
    type: "text",
    value: "heading",
  },
  {
    title: "Slug",
    placeholder: "Enter Blog Slug",
    type: "text",
    value: "slug",
  },
  {
    title: "Is Active",
    placeholder: "Toggle blog active status",
    type: "toggle",
    value: "isActive",
  },
  {
    title: "Content",
    placeholder: "Enter Blog Content",
    type: "richtext",
    value: "content",
  },
  {
    title: "Image",
    placeholder: "Upload Blog Image",
    type: "image",
    value: "image",
  },
  {
    title: "SEO Title",
    placeholder: "Enter SEO Title",
    type: "text",
    value: "seo.title",
    isSeo: true,
  },
  {
    title: "SEO Description",
    placeholder: "Enter SEO Description",
    type: "textarea",
    value: "seo.description",
    isSeo: true,
  },
  {
    title: "SEO Keywords",
    placeholder: "Enter keywords separated by commas",
    type: "keywords",
    value: "seo.keywords",
    isSeo: true,
  },
];

export const CreateEditBlog = () => {
  const navigate = useNavigate();
  const [blog, setBlog] = useState({
    heading: "",
    slug: "",
    content: "",
    image: "",
    isActive: true,
    seo: {
      title: "",
      description: "",
      keywords: [],
    },
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { id } = useParams();

  const handleDeleteBlog = async () => {
    try {
      const response = await fetch(`${API}/Admin/blogs/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        navigate("/blogs");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmDeleteConfirmation = async () => {
    await handleDeleteBlog();
    setShowDeleteConfirmation(false);
  };

  const handleImageUpload = async (image) => {
    try {
      const formData = new FormData();
      formData.append("file", image);
      const response = await fetch(`${API}/image-upload`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      return data.result;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const shouldUploadImage = (image) => {
    if (!image) return false;
    if (typeof image === "string") {
      return false;
    }
    return image instanceof File || image instanceof Blob;
  };

  const getBlog = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/Admin/blogs/${id}`);
      const data = await response.json();
      if (data?.blog) {
        setBlog({
          ...data.blog,
          isActive:
            data.blog.isActive !== undefined ? data.blog.isActive : true,
          seo: data.blog.seo || {
            title: "",
            description: "",
            keywords: [],
          },
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getBlog();
    }
  }, [id]);

  const handleChange = (field, value) => {
    if (field.startsWith("seo.")) {
      const seoField = field.split(".")[1];
      setBlog((prev) => ({
        ...prev,
        seo: {
          ...prev.seo,
          [seoField]: value,
        },
      }));
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    } else {
      setBlog((prev) => {
        const updated = {
          ...prev,
          [field]: value,
        };

        if (field === "heading") {
          updated.slug = value
            ? value.toLowerCase().trim().replace(/\s+/g, "-")
            : "";
        }

        return updated;
      });

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!blog.heading || blog.heading.trim() === "") {
      newErrors.heading = "Heading is required";
    }

    if (!blog.slug || blog.slug.trim() === "") {
      newErrors.slug = "Slug is required";
    }

    if (!blog.content || blog.content.trim() === "") {
      newErrors.content = "Content is required";
    }

    if (!blog.image || blog.image === "") {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateBlog = async () => {
    setLoading(true);
    try {
      let imageUrl = blog.image;
      if (imageFile && shouldUploadImage(imageFile)) {
        imageUrl = await handleImageUpload(imageFile);
        if (!imageUrl) {
          throw new Error("Failed to upload image");
        }
      }

      const keywordsArray = Array.isArray(blog.seo.keywords)
        ? blog.seo.keywords
        : typeof blog.seo.keywords === "string" && blog.seo.keywords.trim()
        ? blog.seo.keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k)
        : [];

      const response = await fetch(`${API}/Admin/blogs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...blog,
          image: imageUrl,
          seo: {
            ...blog.seo,
            keywords: keywordsArray,
          },
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to create blog");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBlog = async () => {
    setLoading(true);
    try {
      let imageUrl = blog.image;
      if (imageFile && shouldUploadImage(imageFile)) {
        imageUrl = await handleImageUpload(imageFile);
        if (!imageUrl) {
          throw new Error("Failed to upload image");
        }
      }

      const keywordsArray = Array.isArray(blog.seo.keywords)
        ? blog.seo.keywords
        : typeof blog.seo.keywords === "string" && blog.seo.keywords.trim()
        ? blog.seo.keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k)
        : [];

      const response = await fetch(`${API}/Admin/blogs/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...blog,
          image: imageUrl,
          seo: {
            ...blog.seo,
            keywords: keywordsArray,
          },
        }),
      });
      const data = await response.json();
      console.log(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateForm()) {
      return;
    }

    try {
      if (id) {
        await handleUpdateBlog();
      } else {
        await handleCreateBlog();
      }
      navigate("/blogs");
    } catch (error) {
      console.log(error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = () => {
          handleChange("image", reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  if (loading && !blog.heading) return <Spin />;

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.heading}>
            {id ? "Edit Blog" : "Create Blog"}
          </div>
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => navigate("/blogs")}
            >
              Cancel
            </button>
            {id && (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Delete Blog
              </button>
            )}
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : id ? "Update Blog" : "Create Blog"}
            </button>
          </div>
        </div>
        {FormFields.map((field) => {
          const fieldValue = field.isSeo
            ? field.value === "seo.keywords"
              ? Array.isArray(blog.seo.keywords)
                ? blog.seo.keywords.join(", ")
                : blog.seo.keywords || ""
              : blog.seo[field.value.split(".")[1]] || ""
            : field.type === "toggle"
            ? blog[field.value] !== undefined
              ? blog[field.value]
              : true
            : blog[field.value] || "";

          return (
            <Input
              key={field.value}
              title={field.title}
              value={fieldValue}
              onChange={(value) => handleChange(field.value, value)}
              placeholder={field.placeholder}
              type={field.type}
              fieldName={field.value}
              error={errors[field.value]}
              submitted={submitted}
              getRootProps={field.type === "image" ? getRootProps : null}
              getInputProps={field.type === "image" ? getInputProps : null}
              isDragActive={field.type === "image" ? isDragActive : false}
              imageFile={field.type === "image" ? imageFile : null}
              imageUrl={field.type === "image" ? blog.image : null}
              setImageFile={field.type === "image" ? setImageFile : null}
              handleImageChange={field.type === "image" ? handleChange : null}
            />
          );
        })}
      </div>
      <DeleteConfirmation
        show={showDeleteConfirmation}
        handleCancel={handleCancelDeleteConfirmation}
        handleConfirm={handleConfirmDeleteConfirmation}
      />
    </div>
  );
};

const Input = ({
  title,
  value,
  onChange,
  placeholder,
  type,
  fieldName,
  error,
  submitted,
  getRootProps,
  getInputProps,
  isDragActive,
  imageFile,
  imageUrl,
  setImageFile,
  handleImageChange,
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const isStringUrl = typeof imageUrl === "string" && imageUrl;

  useEffect(() => {
    if (isStringUrl && !imageFile) {
      setImageLoading(true);
    }
  }, [isStringUrl, imageFile]);

  return (
    <div className={styles.fieldWrapper}>
      <div className={styles.fieldContainer}>
        <div className={styles.field}>{title}:</div>
        {type === "text" ? (
          <input
            className={`${styles.input} ${
              error && submitted ? styles.inputError : ""
            }`}
            name={fieldName}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            type={type}
          />
        ) : type === "textarea" ? (
          <textarea
            className={`${styles.textarea} ${
              error && submitted ? styles.inputError : ""
            }`}
            name={fieldName}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={4}
          />
        ) : type === "keywords" ? (
          <input
            className={`${styles.input} ${
              error && submitted ? styles.inputError : ""
            }`}
            name={fieldName}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            type="text"
          />
        ) : type === "toggle" ? (
          <div className={styles.inputToggle}>
            <div>{value === true ? "Active" : "Inactive"}</div>
            <Toggle
              status={Boolean(value)}
              setStatus={(newValue) => onChange(newValue)}
            />
          </div>
        ) : type === "richtext" ? (
          <div
            className={`${styles.richtextContainer} ${
              error && submitted ? styles.inputError : ""
            }`}
          >
            <TextEditor value={value || ""} onChange={onChange} />
          </div>
        ) : type === "image" ? (
          <div
            {...getRootProps()}
            className={`${styles.dropZone} ${
              isDragActive ? styles.dropZoneActive : ""
            }`}
          >
            <input {...getInputProps()} />
            {imageUrl ? (
              <div className={styles.imageDisplay}>
                {imageLoading && isStringUrl && !imageFile && (
                  <div className={styles.imageLoader}>
                    <div className={styles.spinner}></div>
                  </div>
                )}
                <img
                  src={
                    typeof imageUrl === "string"
                      ? imageUrl
                      : URL.createObjectURL(imageFile)
                  }
                  alt="Blog"
                  className={styles.previewImage}
                  style={{
                    display:
                      imageLoading && isStringUrl && !imageFile
                        ? "none"
                        : "block",
                  }}
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
                <button
                  type="button"
                  className={styles.removeImageButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (handleImageChange) {
                      handleImageChange(fieldName, "");
                    }
                    if (setImageFile) {
                      setImageFile(null);
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <div className={styles.uploadIcon}>
                  <MdCloudUpload size={48} />
                </div>
                <div className={styles.dropText}>
                  {isDragActive
                    ? "Drop the image here"
                    : "Drag and drop an image here, or click to select"}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
      {error && submitted && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};
